import { useEffect, useState, useCallback, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, X, Trash2, CreditCard, Loader2, Paperclip, BadgeCheck, Download, FileCheck2, HandCoins, FileText, Ban } from "lucide-react";
import { toast } from "sonner";
import OSLayout from "../../components/os/OSLayout";
import { api, formatApiError } from "../../lib/api";
import { downloadCFDI } from "../../lib/cfdi";
import TimbrarCFDIModal from "../../components/os/TimbrarCFDIModal";
import PagoREPModal from "../../components/os/PagoREPModal";
import CancelCFDIModal from "../../components/os/CancelCFDIModal";
import { useI18n } from "../../context/I18nContext";

const ease = [0.16, 1, 0.3, 1];
const STATUS_CLS = {
  draft: "bg-navy/10 text-navy/60", sent: "bg-violet/10 text-violet",
  paid: "bg-emerald-500/10 text-emerald-600", overdue: "bg-destructive/10 text-destructive",
};
const inputCls = "w-full bg-white border border-navy/10 rounded-lg px-4 py-3 outline-none focus:border-violet transition-colors";
const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

export default function Finance() {
  const { t, lang } = useI18n();
  const [params, setParams] = useSearchParams();
  const [metrics, setMetrics] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [modal, setModal] = useState(false);
  const [payingId, setPayingId] = useState(null);
  const [fiscalId, setFiscalId] = useState(null);
  const [timbrar, setTimbrar] = useState(null);
  const [rep, setRep] = useState(null);
  const [repCancel, setRepCancel] = useState(null);
  const polled = useRef(false);

  const doCancelRep = async (motive, substitution) => {
    try {
      await api.post("/facturacion/payment/cancel", { source: "finance", invoice_id: repCancel.invId, facturapi_id: repCancel.facturapi_id, motive, substitution });
      toast.success("Complemento de pago cancelado ante el SAT"); setRepCancel(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo cancelar"); }
  };

  const load = useCallback(() => {
    api.get("/finance/overview").then((r) => setMetrics(r.data.metrics)).catch(() => {});
    api.get("/finance/invoices").then((r) => setInvoices(r.data)).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  // Poll payment status on return from Stripe
  const pollStatus = useCallback(async (sessionId, attempts = 0) => {
    if (attempts >= 6) { toast.error(t("os.finance.payFailed")); return; }
    try {
      const { data } = await api.get(`/finance/checkout/status/${sessionId}`);
      if (data.payment_status === "paid") { toast.success(t("os.finance.paySuccess")); load(); return; }
      if (data.status === "expired") { toast.error(t("os.finance.payFailed")); return; }
      toast(t("os.finance.payPending"));
      setTimeout(() => pollStatus(sessionId, attempts + 1), 2000);
    } catch (e) { setTimeout(() => pollStatus(sessionId, attempts + 1), 2000); }
  }, [load, t]);

  useEffect(() => {
    const sid = params.get("session_id");
    if (sid && !polled.current) {
      polled.current = true;
      pollStatus(sid);
      params.delete("session_id");
      setParams(params, { replace: true });
    }
  }, [params, setParams, pollStatus]);

  const pay = async (inv) => {
    setPayingId(inv.id);
    try {
      const { data } = await api.post(`/finance/invoices/${inv.id}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.url;
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); setPayingId(null); }
  };
  const del = async (id) => { await api.delete(`/finance/invoices/${id}`); load(); };
  const uploadFiscal = async (inv, ev) => {
    const list = Array.from(ev.target.files || []); ev.target.value = ""; if (!list.length) return;
    setFiscalId(inv.id);
    const fd = new FormData(); list.forEach((file) => fd.append("files", file));
    try {
      const { data } = await api.post(`/finance/invoices/${inv.id}/fiscal`, fd);
      toast.success("Factura fiscal adjuntada" + (data?.synced_to_accounting ? " · enviada a Contabilidad" : ""));
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "No se pudo subir la factura fiscal"); }
    finally { setFiscalId(null); }
  };
  const dlAtt = async (inv, a) => {
    try {
      const res = await api.get(`/finance/invoices/${inv.id}/attachment/${a.id}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data); const link = document.createElement("a");
      link.href = url; link.download = a.filename || "factura"; link.click(); setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch { toast.error("No se pudo descargar"); }
  };

  return (
    <OSLayout title={t("os.nav.finance")}>
      {timbrar && <TimbrarCFDIModal source="finance" invoiceId={timbrar.id} amount={timbrar.total} defaultName={timbrar.client || ""} onClose={() => setTimbrar(null)} onDone={load} />}
      {rep && <PagoREPModal source="finance" invoiceId={rep.id} total={rep.total} onClose={() => setRep(null)} onDone={load} />}
      {repCancel && <CancelCFDIModal title="Cancelar complemento de pago" subtitle="El REP se cancelará ante el SAT." onConfirm={doCancelRep} onClose={() => setRepCancel(null)} />}
      {!metrics ? (
        <div className="text-navy/40">{t("os.loading")}</div>
      ) : (
        <div className="space-y-8">
          <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-4">
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease }}>
              <div className="overline text-navy/40 mb-2">OMNIA · Finance</div>
              <h2 className="font-editorial text-4xl md:text-5xl tracking-tight">Finance</h2>
              <p className="mt-2 text-navy/50">{t("os.finance.subtitle")}</p>
            </motion.div>
            <button data-testid="finance-new-invoice" onClick={() => setModal(true)}
              className="inline-flex items-center gap-2 rounded-full bg-gradient-violet text-white text-sm px-5 py-3 hover:opacity-90 transition-opacity">
              <Plus className="w-4 h-4" /> {t("os.finance.newInvoice")}
            </button>
          </div>

          <div className="grid grid-cols-2 lg:grid-cols-6 gap-4">
            {metrics.map((m, i) => (
              <motion.div key={m.key} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, ease, delay: i * 0.05 }} data-testid={`finance-kpi-${m.key}`}
                className="bg-white border border-navy/10 rounded-lg p-5">
                <div className="overline text-navy/40 text-[0.6rem]">{lang === "en" ? m.label_en : m.label_es}</div>
                <div className="mt-2 text-2xl font-medium tracking-tight">{m.value}</div>
              </motion.div>
            ))}
          </div>

          {invoices.length === 0 ? (
            <div className="text-navy/40">{t("os.finance.empty")}</div>
          ) : (
            <div className="bg-white border border-navy/10 rounded-lg overflow-x-auto" data-testid="finance-invoices-panel">
              <div className="hidden sm:grid grid-cols-[130px_1.3fr_140px_110px_120px_1fr] gap-3 px-5 py-3 overline text-navy/40 border-b border-navy/10">
                <span>{t("os.finance.cols.number")}</span><span>{t("os.finance.cols.client")}</span><span>{t("os.finance.cols.total")}</span>
                <span>{t("os.finance.cols.status")}</span><span>{t("os.finance.cols.due")}</span><span />
              </div>
              {invoices.map((inv) => (
                <div key={inv.id} className="border-b border-navy/5 last:border-0">
                <div data-testid={`finance-invoice-${inv.id}`} className="grid grid-cols-2 sm:grid-cols-[130px_1.3fr_140px_110px_120px_1fr] gap-3 px-5 py-4 items-center">
                  <div className="font-mono text-xs text-violet">{inv.number}</div>
                  <div className="font-medium text-sm">{inv.client}</div>
                  <div className="text-sm">{money(inv.total)} <span className="text-navy/30 text-xs">MXN</span></div>
                  <div><span className={`text-[11px] px-2 py-1 rounded-full ${STATUS_CLS[inv.status]}`}>{t(`os.finance.status.${inv.status}`)}</span></div>
                  <div className="text-xs text-navy/40">{inv.due_date}</div>
                  <div className="flex items-center gap-2 justify-end flex-wrap">
                    {inv.fiscal || (inv.attachments && inv.attachments.length > 0) ? (
                      <>
                        <span data-testid={`finance-fiscal-badge-${inv.id}`} className="text-[11px] px-2 py-1 rounded-full bg-emerald-500/10 text-emerald-600 inline-flex items-center gap-1"><BadgeCheck className="w-3.5 h-3.5" /> Facturada ✓</span>
                        {inv.fiscal && (
                          <button data-testid={`finance-rep-btn-${inv.id}`} onClick={() => setRep(inv)} title="Registrar pago (complemento REP) para facturas PPD" className="text-[11px] px-2.5 py-1 rounded-full bg-violet/10 text-violet hover:bg-violet/20 transition-colors inline-flex items-center gap-1"><CreditCard className="w-3.5 h-3.5" /> Registrar pago (REP)</button>
                        )}
                      </>
                    ) : (
                      <>
                        <button data-testid={`finance-timbrar-btn-${inv.id}`} onClick={() => setTimbrar(inv)} title="Timbrar CFDI real ante el SAT" className="text-[11px] px-2.5 py-1 rounded-full bg-gradient-violet text-white hover:opacity-90 inline-flex items-center gap-1"><FileCheck2 className="w-3.5 h-3.5" /> Timbrar CFDI</button>
                        <label data-testid={`finance-fiscal-btn-${inv.id}`} title="Subir el PDF/XML fiscal real" className="text-[11px] px-2.5 py-1 rounded-full bg-violet/10 text-violet hover:bg-violet/20 transition-colors inline-flex items-center gap-1 cursor-pointer">
                          {fiscalId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Paperclip className="w-3.5 h-3.5" />} Subir PDF/XML
                          <input type="file" accept=".pdf,.xml" multiple className="hidden" onChange={(ev) => uploadFiscal(inv, ev)} />
                        </label>
                      </>
                    )}
                    {(inv.attachments || []).map((a) => (
                      <button key={a.id} data-testid={`finance-att-${a.id}`} onClick={() => dlAtt(inv, a)} className="text-navy/40 hover:text-violet"><Download className="w-4 h-4" /></button>
                    ))}
                    {inv.status !== "paid" && (
                      <button data-testid={`finance-pay-${inv.id}`} onClick={() => pay(inv)} disabled={payingId === inv.id}
                        className="inline-flex items-center gap-1.5 text-xs rounded-full bg-navy text-white px-3 py-2 hover:bg-navy-soft transition-colors disabled:opacity-60">
                        {payingId === inv.id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <CreditCard className="w-3.5 h-3.5" />} {t("os.finance.pay")}
                      </button>
                    )}
                    <button data-testid={`finance-del-${inv.id}`} onClick={() => del(inv.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
                  </div>
                </div>
                {(inv.payments_rep || []).length > 0 && (
                  <div data-testid={`finance-rep-list-${inv.id}`} className="px-5 pb-3 -mt-1 space-y-1">
                    {inv.payments_rep.map((p) => (
                      <div key={p.facturapi_id} data-testid={`finance-rep-item-${p.facturapi_id}`} className="flex items-center gap-2 text-xs bg-violet/5 rounded-lg px-3 py-2">
                        <HandCoins className="w-3.5 h-3.5 text-violet shrink-0" />
                        <span className="text-navy/60">REP {money(p.amount)}</span>
                        <span className="font-mono text-navy/35 truncate hidden sm:inline">{p.uuid}</span>
                        {p.canceled && <span className="text-[10px] px-2 py-0.5 rounded-full bg-destructive/10 text-destructive">Cancelado</span>}
                        <div className="ml-auto flex items-center gap-1.5">
                          <button data-testid={`finance-rep-pdf-${p.facturapi_id}`} onClick={() => downloadCFDI(`/facturacion/payment/${p.facturapi_id}/download/pdf?source=finance&invoice_id=${inv.id}`, `REP-${p.facturapi_id}.pdf`)} className="px-2 py-1 rounded-full bg-white text-navy/60 hover:text-violet inline-flex items-center gap-1"><FileText className="w-3.5 h-3.5" /> PDF</button>
                          <button data-testid={`finance-rep-xml-${p.facturapi_id}`} onClick={() => downloadCFDI(`/facturacion/payment/${p.facturapi_id}/download/xml?source=finance&invoice_id=${inv.id}`, `REP-${p.facturapi_id}.xml`)} className="px-2 py-1 rounded-full bg-white text-navy/60 hover:text-violet inline-flex items-center gap-1"><Download className="w-3.5 h-3.5" /> XML</button>
                          {!p.canceled && <button data-testid={`finance-rep-cancel-${p.facturapi_id}`} onClick={() => setRepCancel({ invId: inv.id, facturapi_id: p.facturapi_id })} className="px-2 py-1 rounded-full bg-white text-destructive hover:bg-destructive/10 inline-flex items-center gap-1"><Ban className="w-3.5 h-3.5" /></button>}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {modal && <InvoiceModal t={t} onClose={() => setModal(false)} onCreated={() => { setModal(false); load(); }} />}
    </OSLayout>
  );
}

function InvoiceModal({ onClose, onCreated, t }) {
  const [client, setClient] = useState("");
  const [taxRate, setTaxRate] = useState(16);
  const [items, setItems] = useState([{ desc: "", qty: 1, unit: 0 }]);
  const [err, setErr] = useState("");
  const setItem = (i, k, v) => setItems(items.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const addItem = () => setItems([...items, { desc: "", qty: 1, unit: 0 }]);
  const removeItem = (i) => setItems(items.filter((_, idx) => idx !== i));

  const subtotal = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.unit) || 0), 0);
  const tax = subtotal * (Number(taxRate) || 0) / 100;
  const total = subtotal + tax;

  const submit = async (e) => {
    e.preventDefault(); setErr("");
    const valid = items.filter((it) => it.desc && Number(it.unit) > 0);
    if (!valid.length) { setErr(t("os.finance.create.empty")); return; }
    try {
      await api.post("/finance/invoices", {
        client, tax_rate: Number(taxRate) || 0,
        items: valid.map((it) => ({ desc: it.desc, qty: Number(it.qty) || 1, unit: Number(it.unit) || 0 })),
      });
      toast.success("OK"); onCreated();
    } catch (e2) { setErr(formatApiError(e2.response?.data?.detail) || "Error"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy/40 backdrop-blur-sm overflow-y-auto" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, ease }}
        onClick={(e) => e.stopPropagation()} data-testid="finance-invoice-modal" className="w-full max-w-2xl bg-cream rounded-xl p-8 my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-medium">{t("os.finance.create.title")}</h3>
          <button onClick={onClose} className="text-navy/40 hover:text-navy"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input required placeholder={t("os.finance.create.client")} data-testid="finance-input-client" value={client} onChange={(e) => setClient(e.target.value)} className={inputCls} />

          <div className="space-y-2">
            <div className="hidden sm:grid grid-cols-[1fr_80px_120px_40px] gap-2 overline text-navy/40 px-1">
              <span>{t("os.finance.create.desc")}</span><span>{t("os.finance.create.qty")}</span><span>{t("os.finance.create.unit")}</span><span />
            </div>
            {items.map((it, i) => (
              <div key={i} data-testid={`finance-item-${i}`} className="grid grid-cols-[1fr_70px_110px_36px] gap-2 items-center">
                <input placeholder={t("os.finance.create.desc")} value={it.desc} onChange={(e) => setItem(i, "desc", e.target.value)} className={inputCls} />
                <input type="number" value={it.qty} onChange={(e) => setItem(i, "qty", e.target.value)} className={inputCls} />
                <input type="number" value={it.unit} onChange={(e) => setItem(i, "unit", e.target.value)} className={inputCls} />
                <button type="button" onClick={() => removeItem(i)} className="text-navy/30 hover:text-destructive justify-self-center"><X className="w-4 h-4" /></button>
              </div>
            ))}
            <button type="button" data-testid="finance-add-item" onClick={addItem} className="inline-flex items-center gap-2 text-sm text-navy/50 hover:text-violet transition-colors">
              <Plus className="w-4 h-4" /> {t("os.finance.create.addItem")}
            </button>
          </div>

          <div className="flex items-center justify-between gap-4 border-t border-navy/10 pt-4">
            <div className="flex items-center gap-2">
              <label className="text-sm text-navy/50">{t("os.finance.create.taxRate")}</label>
              <input type="number" value={taxRate} onChange={(e) => setTaxRate(e.target.value)} className="w-20 bg-white border border-navy/10 rounded-lg px-3 py-2 outline-none focus:border-violet" />
            </div>
            <div className="text-right text-sm space-y-1">
              <div className="text-navy/50">{t("os.finance.create.subtotal")}: <span className="text-navy">{money(subtotal)}</span></div>
              <div className="text-navy/50">{t("os.finance.create.tax")}: <span className="text-navy">{money(tax)}</span></div>
              <div className="text-lg font-medium">{t("os.finance.create.total")}: {money(total)} <span className="text-navy/30 text-xs">MXN</span></div>
            </div>
          </div>

          {err && <p className="text-sm text-destructive">{err}</p>}
          <button data-testid="finance-invoice-submit" className="w-full rounded-full bg-gradient-violet text-white py-3.5 hover:opacity-90 transition-opacity">{t("os.finance.create.submit")}</button>
        </form>
      </motion.div>
    </div>
  );
}
